import { Box, Slide, Stack } from '@mui/material';

import { useEffect, useState } from 'react';
import { isMobileOnly } from 'react-device-detect';
import { useMenuContext } from '../context';

type Props = {
  children: React.ReactNode;
  width: number;
  minimapHeight?: number;
  menuIsOpen: boolean;
  setMenuIsOpen: (menuIsOpen: boolean) => void;
  onClose?: () => void;
};

export const Container = ({
  children,
  width,
  minimapHeight,
  menuIsOpen, // Needed for the openOnHover feature
  setMenuIsOpen,
  onClose,
}: Props) => {
  const { colors, settings, rtl, open } = useMenuContext();
  const [showMenu, setShowMenu] = useState(false);

  useEffect(() => {
    // Simulating an asynchronous delay
    const delay = setTimeout(() => {
      setShowMenu(true);
    }, 500);

    return () => clearTimeout(delay); // Clean up the timeout on unmount
  }, []);

  const isFullScreen = settings.fullScreenMobile && isMobileOnly;

  return (
    <Box
      dir={rtl ? 'rtl' : 'ltr'}
      onMouseLeave={
        (settings.openOnHoverBm || settings.openOnHoverIot) && menuIsOpen
          ? () => {
              onClose?.();
              setTimeout(() => {
                setMenuIsOpen(false);
              }, 300);
            }
          : undefined
      }
    >
      <Slide in={open} direction="left">
        <Stack
          sx={{
            position: 'fixed',
            top: 0,
            right: 0,
            width: isFullScreen ? '100%' : width,
            maxWidth: isFullScreen ? '100%' : width,
            minWidth: width,
            height: isMobileOnly ? '100vh' : 'auto',
            maxHeight: 'fill-available',
            ...(!isMobileOnly && {
              maxHeight: `calc(100vh - 80px - ${minimapHeight || 0}px)`,
            }),
            zIndex: 999,
            background: colors?.bg,
            visibility: `${showMenu ? 'visible' : 'hidden'} !important`,
            color: colors?.text,
            transition: 'all 225ms cubic-bezier(0, 0, 0.2, 1) 0ms !important',
            borderRadius: '0 0 0 10px',
            scrollbarColor: `${colors?.scrollbar} transparent`,
            scrollbarWidth: 'none',
            msOverflowStyle: 'none',
            '&::-webkit-scrollbar': {
              width: 0,
              height: 0,
              background: 'transparent',
            },
            '&:hover::-webkit-scrollbar': {
              width: '8px',
              height: '8px',
            },
            '&::-webkit-scrollbar-thumb': {
              backgroundColor: colors?.scrollbar,
              borderRadius: '8px',
              backgroundClip: 'padding-box',
              border: '2px solid transparent',
            },
            '&::-webkit-scrollbar-track': {
              background: 'transparent',
            },
            '&:hover': {
              scrollbarWidth: 'thin',
            },
          }}
        >
          {children}
        </Stack>
      </Slide>
    </Box>
  );
};
